import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { emailsService } from '../../services/emailsService';
import { useToastStore } from '../../stores/toastStore';
import { SendEmailStatus } from '../../types/customerFollowUp/enums';
import type { AutoAttachmentDto } from '../../types/customerFollowUp/autoAttachment';
import type { RelanceEmailDto } from '../../types/customerFollowUp/email';
import { supportsAutoAttachments as sourceSupportsAutoAttachments } from '../../utils/relance/supportsAutoAttachments';

/**
 * Whether the body about to be sent differs from the one the AI wrote. Tolerant of a missing body on
 * either side: this is a product-analytics signal, and no send may fail over one.
 */
function wasDraftEdited(sentBody: string | undefined, aiBody: string | undefined): boolean {
    return (sentBody ?? '').trim() !== (aiBody ?? '').trim();
}

/** Which button initiated the in-flight send — drives the per-button spinner. */
export type SendingAction = 'send' | 'sendAndNext';

interface UseEmailSendFlowParams {
    relanceId: string;
    getDraft: () => RelanceEmailDto;
    getManualFiles: () => File[];
    getAiDraft: () => RelanceEmailDto | null;
    isValid: boolean;
    automaticAttachments: AutoAttachmentDto[];
    setAutomaticAttachments: React.Dispatch<React.SetStateAction<AutoAttachmentDto[]>>;
    /** Whether the reminder carries its Transphere payment links. Only the flag is sent, never the URLs. */
    includePaymentLinks: boolean;
    setLoading: (loading: boolean) => void;
    onSendingChange?: (sending: boolean) => void;
    setSent: (sent: boolean) => void;
    setSendingAction: (action: SendingAction | null) => void;
}

export function useEmailSendFlow({
    relanceId,
    getDraft,
    getManualFiles,
    getAiDraft,
    isValid,
    automaticAttachments,
    setAutomaticAttachments,
    includePaymentLinks,
    setLoading,
    onSendingChange,
    setSent,
    setSendingAction,
}: UseEmailSendFlowParams) {
    const { t } = useTranslation();
    const showToast = useToastStore(s => s.showToast);

    const [pendingDeleteAutoAttachmentName, setPendingDeleteAutoAttachmentName] = useState<string | null>(null);
    const [attachmentFetchFailedFiles, setAttachmentFetchFailedFiles] = useState<string[] | null>(null);
    const [showNoAttachmentsWarning, setShowNoAttachmentsWarning] = useState(false);
    const pendingOnCompleteRef = useRef<(() => void) | undefined>(undefined);

    const setSending = (sending: boolean) => {
        setLoading(sending);
        onSendingChange?.(sending);
    };

    const doSend = async (onComplete?: () => void, attachmentsOverride?: AutoAttachmentDto[]) => {
        setSending(true);
        // When we surface the "attachment fetch failed" modal the send isn't over —
        // the user confirms and we retry, so the originally-clicked button must keep
        // its spinner. Only clear sendingAction once the send reaches a terminal state.
        let promptingForRetry = false;
        try {
            const draft = getDraft();
            const aiDraft = getAiDraft();
            const attachments = attachmentsOverride ?? automaticAttachments;
            const result = await emailsService.sendEmail(relanceId, {
                draftId: null,
                draft,
                autoAttachmentIds: attachments.map(a => a.id),
                includePaymentLinks,
                // A tone switch rewrites the body, so it reads as edited here — which is accurate, and
                // the tone pair tells the two apart on the analytics side.
                draftEdited: aiDraft ? wasDraftEdited(draft.bodyTone, aiDraft.bodyTone) : undefined,
                aiSelectedTone: aiDraft?.selectedTone,
            }, getManualFiles());
            if (result.status === SendEmailStatus.AUTO_ATTACHMENT_FAILED) {
                promptingForRetry = true;
                pendingOnCompleteRef.current = onComplete;
                setAttachmentFetchFailedFiles(result.failedAutoAttachmentFileNames ?? []);
                return;
            }
            if (result.status === SendEmailStatus.TOTAL_ATTACHMENT_TOO_LARGE) {
                showToast('error', t(TranslationKey.CustomerFollowUp.Email.Errors.TotalAttachmentsTooLarge));
                return;
            }
            const succeeded = result.status !== SendEmailStatus.FAILED;
            showToast(
                succeeded ? 'success' : 'error',
                t(succeeded
                    ? TranslationKey.CustomerFollowUp.Toast.Success
                    : TranslationKey.CustomerFollowUp.Toast.Failure)
            );
            if (succeeded) {
                setSent(true);
                onComplete?.();
            }
        } catch {
            showToast('error', t(TranslationKey.CustomerFollowUp.Toast.Failure));
        } finally {
            setSending(false);
            if (!promptingForRetry) setSendingAction(null);
        }
    };

    const handleSend = (onComplete?: () => void) => {
        if (!isValid) return;
        // The warning says the customer will receive "ni facture ni état de compte", which reads as "you
        // forgot to attach something". On Acomba desktop there is nothing to forget: the source produces
        // no automatic documents at all, so it would fire on every single send and mean nothing.
        const supportsAutoAttachments = sourceSupportsAutoAttachments();
        if (supportsAutoAttachments && automaticAttachments.length === 0 && getManualFiles().length === 0) {
            pendingOnCompleteRef.current = onComplete;
            setShowNoAttachmentsWarning(true);
            return;
        }
        void doSend(onComplete);
    };

    const handleDeleteAutoAttachmentConfirm = () => {
        if (pendingDeleteAutoAttachmentName) {
            const att = automaticAttachments.find(a => a.fileName === pendingDeleteAutoAttachmentName);
            if (att) setAutomaticAttachments(prev => prev.filter(a => a.id !== att.id));
            setPendingDeleteAutoAttachmentName(null);
        }
    };

    const handleNoAttachmentsConfirm = () => {
        setShowNoAttachmentsWarning(false);
        void doSend(pendingOnCompleteRef.current);
    };

    const handleAttachmentFetchFailedConfirm = () => {
        const failedSet = new Set(attachmentFetchFailedFiles ?? []);
        const remaining = automaticAttachments.filter(a => !failedSet.has(a.fileName));
        setAutomaticAttachments(remaining);
        setAttachmentFetchFailedFiles(null);
        void doSend(pendingOnCompleteRef.current, remaining);
    };

    return {
        handleSend,
        pendingDeleteAutoAttachmentName,
        setPendingDeleteAutoAttachmentName,
        handleDeleteAutoAttachmentConfirm,
        attachmentFetchFailedFiles,
        setAttachmentFetchFailedFiles,
        handleAttachmentFetchFailedConfirm,
        showNoAttachmentsWarning,
        setShowNoAttachmentsWarning,
        handleNoAttachmentsConfirm,
    };
}
